import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Helmet } from 'react-helmet-async';
import { ArrowLeft, Calendar, Loader2, User, Link2, Target } from 'lucide-react';

const BlogDetail = () => {
  const { slug } = useParams<{ slug: string }>();
  const [blog, setBlog] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
    // Port 2025 ensure karna
    axios.get(`https://www.projectinfotech.in/api/blogs/${slug}`)
      .then(res => {
        setBlog(res.data);
        setLoading(false);
      })
      .catch(err => {
        console.error("Error fetching blog detail", err);
        setLoading(false);
      });
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center text-red-600 bg-gray-50">
        <Loader2 className="animate-spin mb-4" size={48} />
        <span className="font-bold text-lg text-gray-700">Loading Article...</span>
      </div>
    );
  }

  if (!blog) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 px-4 text-center">
        <Target size={64} className="text-gray-300 mb-6" />
        <h2 className="text-4xl font-extrabold text-gray-900 mb-4 font-aligran">Article Not Found</h2>
        <p className="text-gray-600 mb-8 max-w-md">The blog post you're looking for might have been moved, deleted, or the URL is incorrect.</p>
        <Link to="/blogs" className="bg-red-600 text-white font-bold py-3 px-8 rounded-xl flex items-center gap-2 hover:bg-red-700 transition transform active:scale-95 shadow-lg shadow-red-500/20">
          <ArrowLeft size={18}/> Back to All Blogs
        </Link>
      </div>
    );
  }

  return (
    <div className="bg-white text-gray-900 font-sans min-h-screen">
    // BlogDetail.tsx ke Helmet section mein ye replace karo
<Helmet>
  <title>{blog.metaTitle || blog.title} | PIT Institute</title>
  <meta name="description" content={blog.metaDescription} />
  <link rel="canonical" href={`https://www.projectinfotech.in/blog/${blog.slug}`} />
  
  {/* Open Graph / Facebook */}
  <meta property="og:type" content="article" />
  <meta property="og:title" content={blog.metaTitle || blog.title} />
  <meta property="og:description" content={blog.metaDescription} />
  <meta property="og:image" content={blog.imageUrl} />
  <meta property="og:url" content={`https://www.projectinfotech.in/blog/${blog.slug}`} />

  {/* Article Schema for Google */}
  <script type="application/ld+json">
    {JSON.stringify({
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      "headline": blog.title,
      "image": [blog.imageUrl],
      "datePublished": blog.createdAt,
      "author": [{
          "@type": "Organization",
          "name": "PIT Institute",
          "url": "https://projectinfotech.in"
      }],
      "description": blog.metaDescription
    })}
  </script>
</Helmet>

      {/* Top Bar - Back Navigation */}
      <div className="border-b border-gray-100 bg-gray-50/50 sticky top-0 z-10 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
            <Link to="/blogs" className="inline-flex items-center text-gray-600 font-medium text-sm hover:text-red-600 transition group">
                <ArrowLeft size={18} className="mr-2 group-hover:-translate-x-1 transition-transform" /> Back to Blog Feed
            </Link>
            <div className="text-xs text-gray-400 font-mono hidden sm:block">PIT_TECH_INSIGHTS // {blog.slug}</div>
        </div>
      </div>

      {/* Main Content Area */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-12 md:py-16">
        <div className="flex flex-col lg:flex-row lg:gap-12 xl:gap-16">
          
          {/* Left Column: Article Content */}
          <div className="flex-grow lg:w-2/3">
            <article>
              {/* Meta Info Above Title */}
              <div className="flex items-center gap-4 text-sm text-gray-500 mb-5 font-medium">
                  <span className="bg-red-50 text-red-700 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider">
                      Tech Insights
                  </span>
                  <span className="flex items-center gap-1.5">
                      <Calendar size={16} className="text-gray-400"/> 
                      {new Date(blog.createdAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                  </span>
                  <span className="flex items-center gap-1.5">
                      <User size={16} className="text-gray-400"/> By PIT Expert
                  </span>
              </div>

              {/* Title - The main SEO Catchy H1 */}
              <h1 className="text-4xl md:text-5xl font-extrabold text-gray-950 leading-tight mb-10 font-aligran tracking-tight">
                {blog.title}
              </h1>

              {/* Flex container for content and side image */}
              <div className="flex flex-col md:flex-row-reverse md:gap-8 items-start mb-12 border-t border-gray-100 pt-10">
                  
                  {/* Side Image - Compact and Professional */}
                  {blog.imageUrl && (
                    <div className="w-full md:w-2/5 lg:w-1/3 mb-8 md:mb-0 flex-shrink-0">
                        <img 
                            src={blog.imageUrl} 
                            alt={blog.imageAlt || blog.title} 
                            className="w-full h-auto object-cover rounded-2xl shadow-lg border-4 border-white aspect-[4/3]"
                        />
                        {/* Compact Unsplash Credit directly under image */}
                        <div className="text-center mt-2.5 text-xs text-gray-400 font-medium">
                            Photo from Unsplash
                        </div>
                    </div>
                  )}

                  {/* Intro/Meta Description */}
                  <div className="flex-grow prose prose-xl prose-gray text-gray-700 leading-relaxed font-serif italic border-l-4 border-red-100 pl-6 mb-10 md:mb-0">
                      <p>{blog.metaDescription}</p>
                  </div>
              </div>

              {/* Core Blog Content (HTML Rendered from Database) */}
              <div className="prose prose-lg prose-red text-gray-800 leading-relaxed max-w-none 
                  prose-headings:font-aligran prose-headings:font-bold prose-headings:text-gray-950
                  prose-h2:text-3xl prose-h2:mt-12 prose-h2:mb-6 prose-h2:border-b-2 prose-h2:border-gray-100 prose-h2:pb-3
                  prose-h3:text-2xl prose-h3:mt-8 prose-h3:mb-4
                  prose-p:mb-6
                  prose-ul:list-disc prose-ul:pl-6 prose-ul:mb-6 prose-li:mb-2
                  prose-strong:text-gray-950 prose-strong:font-semibold
                  prose-code:text-red-700 prose-code:bg-red-50 prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded-md prose-code:font-mono prose-code:text-sm
              ">
                <div dangerouslySetInnerHTML={{ __html: blog.content }}></div>
              </div>

            </article>
          </div>

          {/* Right Column: Sticky Sidebar for Lead Generation & Credits */}
          <aside className="lg:w-1/3 mt-16 lg:mt-0 flex-shrink-0">
            <div className="sticky top-28 space-y-8">
                
                {/* Lead Generation CTA Block (The main goal of this blog) */}
                <div className="bg-gray-950 text-white p-8 rounded-3xl shadow-2xl shadow-gray-950/20 border-t-4 border-red-600">
                    <Target size={40} className="text-red-500 mb-6" />
                    <h3 className="text-2xl font-bold mb-4 font-aligran text-white leading-tight">Master this topic practically?</h3>
                    <p className="text-gray-300 mb-8 text-base leading-relaxed">
                        Join PIT Institute's highly-rated training programs. Get 100% placement support and learn directly from industry experts in Ghaziabad.
                    </p>
                    <Link to="/" className="w-full text-center bg-red-600 hover:bg-red-700 text-white font-extrabold py-4 px-8 rounded-xl transition inline-block shadow-lg shadow-red-600/30 transform active:scale-95 text-lg">
                        Explore Courses
                    </Link>
                </div>

                {/* Tags / Related Info (Optional, but looks professional) */}
                <div className="bg-gray-50 border border-gray-100 p-7 rounded-2xl">
                    <h4 className="text-sm font-bold uppercase text-gray-500 tracking-wider mb-5 flex items-center gap-2">
                        <Link2 size={16} className="text-red-500" /> Topic Tags
                    </h4>
                    <div className="flex flex-wrap gap-2.5">
                        {blog.slug.split('-').slice(0, 3).map((tag: string) => (
                            <span key={tag} className="bg-white text-gray-700 px-3 py-1.5 rounded-lg text-sm font-medium border border-gray-100 capitalize">
                                {tag}
                            </span>
                        ))}
                    </div>
                </div>

            </div>
          </aside>

        </div>
      </main>
      
      {/* Simple Professional Footer inside blog */}
      <footer className="border-t border-gray-100 bg-gray-50/50 mt-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 text-center text-gray-500 text-sm font-medium">
              &copy; {new Date().getFullYear()} PIT Institute. All rights reserved. Technology Education Partner in Ghaziabad.
          </div>
      </footer>

    </div>
  );
};

export default BlogDetail;